import { IS_SIDEBAR_PANEL_ENABLED } from '@/components/constants/flags';
import { getSidebarPanelOverlayTriggerProps } from '@/components/layout/Navigation/sidebar-panel/sidebar-overlay-constants';
import { OfflineGatedTooltip } from '@/components/offline-recording/offline-gated-tooltip';
import { sessionUrgencyMessages } from '@/components/ui/session-urgency/messages';
import { useIsSessionUrgencyEnabled } from '@/features/flags';
import { useOfflineGating } from '@/hooks/desktop/useOfflineGating';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { Label } from '@heidi/design-system/label';
import { RadioGroup, RadioGroupItem } from '@heidi/design-system/radio-group';
import { TypographyP2 } from '@heidi/design-system/typography';
import { type TranslationKey, useTranslations } from '@heidi/runtime/i18n';
import {
  DEFAULT_SORT_BY,
  sessionsSortByAtom,
  sessionsSortOrderAtom,
  type SortOption,
} from '@heidi/session/data';
import { useAtom } from 'jotai';
import { ArrowDownUp, Check } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

const baseSortOptions: { value: SortOption; label: TranslationKey }[] = [
  { value: 'createdAt', label: 'sessionToolbar.sort.dateCreated' },
  { value: 'updatedAt', label: 'sessionToolbar.sort.dateUpdated' },
  { value: 'name', label: 'sessionToolbar.sort.name' },
  { value: 'status', label: 'sessionToolbar.sort.status' },
];

export const SessionsSort = () => {
  const { t } = useTranslations();
  const intl = useIntl();
  const { shouldGateControls: isOfflineGated } = useOfflineGating();
  const [sortBy, setSortBy] = useAtom(sessionsSortByAtom);
  const [sortOrder, setSortOrder] = useAtom(sessionsSortOrderAtom);
  const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();
  const isSessionUrgencyEnabled = useIsSessionUrgencyEnabled();

  const [isOpen, setIsOpen] = useState(false);
  const optionIdPrefix = useId();

  // `sort_by=urgency` isn't implemented server-side yet — the urgent-floating
  // sort runs client-side in `useSessionsFilteredList`. Gate the option behind
  // the same flag as every other urgency surface, floated to the bottom.
  const sortOptions = useMemo(() => {
    const options: { value: SortOption; label: string }[] = baseSortOptions.map(
      (option) => ({ value: option.value, label: t(option.label) })
    );
    if (isSessionUrgencyEnabled) {
      options.push({
        value: 'urgency',
        label: intl.formatMessage(sessionUrgencyMessages.urgency),
      });
    }
    return options;
  }, [t, intl, isSessionUrgencyEnabled]);

  // If the flag flips off while 'urgency' is the active sort, fall back to the
  // default so the atom never points at a hidden (and server-unsupported) option.
  useEffect(() => {
    if (!isSessionUrgencyEnabled && sortBy === 'urgency') {
      setSortBy(DEFAULT_SORT_BY);
    }
  }, [isSessionUrgencyEnabled, sortBy, setSortBy]);

  // Close the dropdown if we go offline while it's open — the gated branch
  // renders a disabled trigger, so a stale-open menu would otherwise linger.
  useEffect(() => {
    if (isOfflineGated && isOpen) setIsOpen(false);
  }, [isOfflineGated, isOpen]);

  const isSortApplied =
    sortBy !== sessionsSortByAtom.init || sortOrder !== sessionsSortOrderAtom.init;

  const sortTrigger = (
    <Button
      data-testid="session-list-sort-button"
      size="xxs"
      iconOnly
      variant={isSidebarPanelEnabled ? 'panel_ghost' : 'ghost'}
      disabled={isOfflineGated}
      data-active={isSidebarPanelEnabled ? isSortApplied || isOpen : undefined}
      className={
        !isSidebarPanelEnabled
          ? cn(
              'text-text-secondary',
              isOpen && 'bg-background-secondary text-text-primary'
            )
          : undefined
      }
      aria-label="Sort sessions"
    >
      <ArrowDownUp aria-hidden="true" />
    </Button>
  );

  // Offline: sorting the session list is gated alongside search/filter as part
  // of the offline grey-out sweep. Disabled button wrapped in a span so the
  // tooltip still shows (native disabled buttons swallow pointer events).
  if (isOfflineGated) {
    return <OfflineGatedTooltip isOfflineGated>{sortTrigger}</OfflineGatedTooltip>;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger
        {...getSidebarPanelOverlayTriggerProps(isSidebarPanelEnabled)}
        asChild
      >
        {sortTrigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="start"
        className="flex w-[244px] flex-col gap-1 px-1.5 py-1"
      >
        <RadioGroup
          value={sortBy}
          onValueChange={(value) => setSortBy(value as SortOption)}
          className="flex flex-col gap-0"
        >
          {sortOptions.map((option) => (
            <Label
              key={option.value}
              htmlFor={`${optionIdPrefix}-${option.value}`}
              className="flex items-center gap-2.5 rounded-sm px-2 py-1.5 hover:bg-background-secondary"
            >
              <RadioGroupItem
                value={option.value}
                id={`${optionIdPrefix}-${option.value}`}
              />
              <TypographyP2>{option.label}</TypographyP2>
            </Label>
          ))}
        </RadioGroup>
        <DropdownMenuSeparator className="-mx-0 my-0 w-full" />
        <div className="flex flex-col">
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSortOrder('asc');
            }}
            className="justify-between px-2 py-1.5"
          >
            <TypographyP2>{t('sessionToolbar.sort.ascending')}</TypographyP2>
            {sortOrder === 'asc' && <Check size={16} />}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSortOrder('desc');
            }}
            className="justify-between px-2 py-1.5"
          >
            <TypographyP2>{t('sessionToolbar.sort.descending')}</TypographyP2>
            {sortOrder === 'desc' && <Check size={16} />}
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
